// useDropdownMultiple.ts
import { computed, ref, type Ref } from 'vue'
import type { DropdownId } from '../dropdown'
import type { DropdownMultipleItem } from './BasicDropdownMultiple.types'

export type LigneAffichage<T> = {
  niveau: number
  label: string
  estGroupe: boolean
  estSelectionne: boolean
  toutSelectionne: boolean
  item?: T
  cleGroupe: string
  itemsDuGroupe: T[]
}

/** Detecte si les items sont au format simple { id, label } */
function isFormatSimple(items: unknown[]): items is { id: DropdownId; label: string }[] {
  if (!items.length) return false
  const first = items[0] as Record<string, unknown>
  return 'id' in first && 'label' in first
}

/** Convertit le format simple { id, label } vers le format multi-niveau { [id]: label } */
function convertirFormatSimple(items: { id: DropdownId; label: string }[]): DropdownMultipleItem[] {
  return items.map((item) => ({ [String(item.id)]: item.label }))
}

/**
 * Detecte le nombre de niveaux maximum parmi tous les items.
 * Chaque propriete represente un niveau (cle = ID, valeur = label).
 */
function detectNbNiveaux<T extends DropdownMultipleItem>(items: T[]): number {
  if (!items.length) return 1
  return Math.max(...items.map((item) => Object.keys(item as object).length))
}

export function useDropdownMultiple<T extends DropdownMultipleItem>(params: {
  items: () => T[]
  selection: Ref<DropdownId[]>
  keyId?: () => (keyof T)[] | undefined
  keyLabel?: () => (keyof T)[] | undefined
  itemId?: () => ((item: T) => DropdownId) | undefined
}) {
  const isOpen = ref(false)

  /** Items normalises (convertis si format simple detecte et pas de keyId fourni) */
  const itemsNormalises = computed(() => {
    const raw = params.items()
    // Ne pas convertir si keyId est fourni (l'appelant gere lui-meme la structure)
    if (!params.keyId?.() && isFormatSimple(raw)) {
      return convertirFormatSimple(raw) as T[]
    }
    return raw
  })

  const nbNiveaux = computed(() => {
    // Si keyId est fourni, on l'utilise
    if (params.keyId?.()) return params.keyId()!.length
    // Sinon on detecte le nombre de niveaux
    return detectNbNiveaux(itemsNormalises.value)
  })
  const estMultiNiveau = computed(() => nbNiveaux.value > 1)

  /**
   * Accede a l'ID d'un item a un niveau donne.
   * - Si keyId est fourni : utilise le nom de cle
   * - Sinon : utilise l'indice (cle = ID dans la structure dynamique)
   */
  const getId = (item: T, niveau: number) => {
    if (params.keyId?.()) {
      return item[params.keyId()![niveau]!]
    }
    // Structure dynamique : la cle a l'indice N est l'ID du niveau N
    return Object.keys(item)[niveau]
  }

  /**
   * Accede au label d'un item a un niveau donne.
   * - Si keyLabel est fourni : utilise le nom de cle
   * - Sinon : utilise l'indice (valeur = label dans la structure dynamique)
   */
  const getLabel = (item: T, niveau: number) => {
    if (params.keyLabel?.()) {
      return String(item[params.keyLabel()![niveau]!] ?? '')
    }
    // Structure dynamique : la valeur a l'indice N est le label du niveau N
    return String(Object.values(item)[niveau] ?? '')
  }

  /**
   * Retourne le nombre de cles (niveaux) d'un item.
   */
  const getNbCles = (item: T) => Object.keys(item as object).length

  const getItemId = (item: T): DropdownId => {
    const custom = params.itemId?.()
    if (custom) return custom(item)
    // Utilise le dernier niveau de l'item (pas le niveau max global)
    const dernierNiveauItem = getNbCles(item) - 1
    return getId(item, dernierNiveauItem) as DropdownId
  }

  const selectedSet = computed(() => new Set(params.selection.value))
  const estSelectionne = (item: T) => selectedSet.value.has(getItemId(item))

  /**
   * Genere une cle unique pour identifier un groupe a un niveau donne.
   * Concatene les IDs de tous les niveaux jusqu'a niveauMax.
   * Ex: item {parentId:'FPE', id:'CDPI'} niveau 0 -> 'FPE', niveau 1 -> 'FPE|CDPI'
   */
  const getCleGroupe = (item: T, niveauMax: number) => {
    const parts: string[] = []
    for (let i = 0; i <= niveauMax; i++) parts.push(String(getId(item, i)))
    return parts.join('|')
  }

  /**
   * Pre-indexe les items par groupe pour chaque niveau.
   * Evite de recalculer les items d'un groupe a chaque rendu (O(n) -> O(1)).
   * maps[niveau].get(cleGroupe) retourne tous les items de ce groupe.
   */
  const indexGroupes = computed(() => {
    const items = itemsNormalises.value
    const dernier = nbNiveaux.value - 1

    // Filtre les items sans ID valide (au dernier niveau de l'item, pas global)
    const itemsValides = items.filter((it) => {
      const nbCles = getNbCles(it)
      const dernierNiveauItem = nbCles - 1
      const last = getId(it, dernierNiveauItem)
      return last !== undefined && last !== null && last !== ''
    })

    // Cree une Map par niveau (sauf le dernier qui n'a pas de groupes)
    const maps: Array<Map<string, T[]>> = Array.from({ length: Math.max(0, dernier) }, () => new Map())

    // Indexe chaque item dans les Maps de ses groupes parents
    // Note: les items a 1 niveau ne sont pas indexes (pas de groupe parent)
    for (const it of itemsValides) {
      const nbCles = getNbCles(it)
      for (let niveau = 0; niveau < Math.min(dernier, nbCles - 1); niveau++) {
        const key = getCleGroupe(it, niveau)
        const arr = maps[niveau]!.get(key)
        if (arr) arr.push(it)
        else maps[niveau]!.set(key, [it])
      }
    }

    return { itemsValides, maps, dernier }
  })

  /**
   * Construit la liste plate des lignes a afficher (groupes + feuilles).
   * Parcourt les items et insere les en-tetes de groupe au premier passage.
   *
   * Structure resultante pour 2 niveaux :
   *   [Groupe FPE] <- niveau 0, estGroupe=true
   *     [Item CDPI] <- niveau 1, estGroupe=false
   *     [Item VAE]
   *   [Groupe CPRO]
   *     [Item Prodiat]
   *
   * Les items a 1 niveau sont affiches directement comme feuilles au niveau 0
   * (sans en-tete de groupe).
   */
  const lignesAAfficher = computed((): LigneAffichage<T>[] => {
    const { itemsValides, maps, dernier } = indexGroupes.value
    if (!itemsValides.length || nbNiveaux.value < 1) return []

    const lignes: LigneAffichage<T>[] = []
    const groupesVus = new Set<string>()

    for (const item of itemsValides) {
      const nbCles = getNbCles(item)

      // Items a 1 niveau : afficher directement comme feuille au niveau 0
      if (nbCles === 1) {
        lignes.push({
          niveau: 0,
          label: getLabel(item, 0),
          estGroupe: false,
          estSelectionne: estSelectionne(item),
          toutSelectionne: false,
          item,
          cleGroupe: getCleGroupe(item, 0),
          itemsDuGroupe: [],
        })
        continue
      }

      // Insere les lignes de groupe (niveaux 0 a n-1) si pas encore vues
      for (let niveau = 0; niveau < dernier; niveau++) {
        const cleGroupe = getCleGroupe(item, niveau)
        if (groupesVus.has(cleGroupe)) continue
        groupesVus.add(cleGroupe)

        const itemsDuGroupe = maps[niveau]!.get(cleGroupe) ?? []
        const toutSelectionne = itemsDuGroupe.length > 0 && itemsDuGroupe.every(estSelectionne)

        lignes.push({
          niveau,
          label: getLabel(item, niveau),
          estGroupe: true,
          estSelectionne: false,
          toutSelectionne,
          cleGroupe,
          itemsDuGroupe,
        })
      }

      // Insere la ligne feuille (dernier niveau, selectionnable)
      lignes.push({
        niveau: dernier,
        label: getLabel(item, dernier),
        estGroupe: false,
        estSelectionne: estSelectionne(item),
        toutSelectionne: false,
        item,
        cleGroupe: getCleGroupe(item, dernier),
        itemsDuGroupe: [],
      })
    }

    return lignes
  })

  const basculerItem = (item: T) => {
    const id = getItemId(item)
    const newSelection = selectedSet.value.has(id)
      ? params.selection.value.filter((x) => x !== id)
      : [...params.selection.value, id]
    params.selection.value = newSelection
  }

  const basculerGroupe = (ligne: LigneAffichage<T>) => {
    const idsDuGroupe = ligne.itemsDuGroupe.map(getItemId)
    const autres = params.selection.value.filter((id) => !idsDuGroupe.includes(id))
    params.selection.value = ligne.toutSelectionne ? autres : [...autres, ...idsDuGroupe]
  }

  const onItemClick = (ligne: LigneAffichage<T>) => {
    if (ligne.estGroupe) basculerGroupe(ligne)
    else basculerItem(ligne.item!)
  }

  /**
   * Genere le resume textuel de la selection (affiche dans le champ ferme).
   * Optimise l'affichage en evitant les redondances :
   *   - Si un groupe est complet -> affiche le groupe, pas ses enfants
   *   - Si un parent est deja affiche -> n'affiche pas ses sous-groupes
   *
   * Ex: FPE complet + CPRO/Prodiat -> "FPE, Prodiat" (pas "FPE, CDPI, VAE, Prodiat")
   */
  const resume = computed(() => {
    const libelles: string[] = []

    // 1. Collecte tous les groupes entierement selectionnes
    const groupesComplets = new Set<string>()
    for (const ligne of lignesAAfficher.value) {
      if (ligne.estGroupe && ligne.toutSelectionne) groupesComplets.add(ligne.cleGroupe)
    }

    for (const ligne of lignesAAfficher.value) {
      if (ligne.estGroupe && ligne.toutSelectionne) {
        // 2a. Groupe complet : l'affiche seulement si aucun parent n'est deja complet
        // Ex: si "FPE" est complet, on n'affiche pas "FPE|SousGroupe" meme s'il est complet
        let parentDejaAffiche = false
        for (let i = 0; i < ligne.niveau; i++) {
          const cleParent = ligne.cleGroupe.split('|').slice(0, i + 1).join('|')
          if (groupesComplets.has(cleParent)) {
            parentDejaAffiche = true
            break
          }
        }
        if (!parentDejaAffiche) libelles.push(ligne.label)
      } else if (!ligne.estGroupe && ligne.estSelectionne) {
        // 2b. Item selectionne : l'affiche seulement si aucun groupe parent n'est complet
        // Ex: si "FPE" est complet, on n'affiche pas "CDPI" individuellement
        let parentComplet = false
        for (let i = 0; i < ligne.niveau; i++) {
          const cleParent = ligne.cleGroupe.split('|').slice(0, i + 1).join('|')
          if (groupesComplets.has(cleParent)) {
            parentComplet = true
            break
          }
        }
        if (!parentComplet) libelles.push(ligne.label)
      }
    }

    return libelles.join(', ')
  })

  return {
    isOpen,
    nbNiveaux,
    estMultiNiveau,
    lignesAAfficher,
    resume,
    onItemClick,
  }
}
